import { Text } from '@velocity/ui'
import * as R from 'ramda'
import * as React from 'react'

import {
  useBusinessRules,
  useContent,
  useQuery,
} from '@ngb-frontend/shared/context'
import { Feature, StepVariants } from '@ngb-frontend/shared/types'
import { FullScreenLoader } from '@ngb-frontend/shared/ui'
import { hasAddress, useDriver, useVehicle } from '@ngb-frontend/shared/utils'

import { useLocationStepStyles } from './LocationStep.styled'
import { LocationForm } from '../../forms/LocationForm/LocationForm'

import type { LocationFormResult } from '../../forms/LocationForm/LocationForm'
import type {
  Address,
  LocationChoice,
  LocationStepPrerequisiteData,
  LocationStepValues,
  StepProps,
} from '@ngb-frontend/shared/types'

const addressParts: (keyof Address)[] = [
  'street',
  'house_number',
  'house_number_suffix',
  'postcode',
  'city',
]

const isFilledAddress = (address?: Partial<Address>): address is Address =>
  !!address && addressParts.some((part) => !!address[part])

const isSameAddress = (a?: Partial<Address>, b?: Partial<Address>) =>
  addressParts.every((part) => (a?.[part] || '') === (b?.[part] || ''))

export const getInitialLocation = (
  earlier: LocationStepValues | undefined,
  bookingAddress: Address | undefined,
  knownAddress: Address | undefined,
): { choice?: LocationChoice; address?: Address } => {
  if (isFilledAddress(earlier?.otherLocation)) {
    return { choice: 'other', address: earlier?.otherLocation }
  }

  const address = isFilledAddress(earlier?.location)
    ? earlier?.location
    : bookingAddress

  if (!isFilledAddress(address)) return {}

  return isSameAddress(address, knownAddress)
    ? { choice: 'known' }
    : { choice: 'other', address }
}

const LocationStep: React.FC<
  StepProps<LocationStepValues, LocationStepPrerequisiteData>
> = ({ onNextStep, onUpdateStep, stepData, stepVariant, booking }) => {
  const query = useQuery()
  const rules = useBusinessRules()
  const { data: vehicle } = useVehicle(query)
  const { data: driver } = useDriver(
    vehicle?.driverIdentifier || query?.licensePlate,
    query,
  )

  const c = useContent()

  const initialRef = React.useRef<ReturnType<typeof getInitialLocation>>()
  if (driver && !initialRef.current) {
    initialRef.current = getInitialLocation(
      Array.isArray(stepData) ? undefined : stepData,
      booking?.driver.pickup_address,
      driver.work_address,
    )
  }

  // NB: Both keys are always written: useFlow merges step data, and the
  // steps after this one read `otherLocation || location`.
  const handleSubmit = React.useCallback(
    (result: LocationFormResult) => {
      onUpdateStep(
        result.choice === 'known'
          ? { location: driver?.work_address, otherLocation: undefined }
          : { location: undefined, otherLocation: result.address },
      )
      onNextStep()
    },
    [driver?.work_address, onNextStep, onUpdateStep],
  )

  const withPickup = stepVariant === StepVariants.LocationWithPickup
  const showPickupWindow =
    withPickup && !!rules.dates(Feature.MnR).pickup.fixedTime
  const classes = useLocationStepStyles()

  if (!driver) return <FullScreenLoader />

  return (
    <>
      {showPickupWindow && (
        <Text
          variant="200"
          margin={{ bottom: '07' }}
          data-e2e-component="pickup-window"
        >
          {c('fields.date.hint')}
        </Text>
      )}
      <div
        className={
          withPickup && !showPickupWindow ? classes.withoutLine : undefined
        }
      >
        <LocationForm
          groupLabel={c(
            withPickup
              ? 'steps.locationWithPickup.title'
              : 'steps.location.title',
          )}
          testIdPrefix={stepVariant || StepVariants.Location}
          knownAddress={driver.work_address}
          otherDetails={c(
            withPickup
              ? 'steps.locationWithPickup.specifyAddress'
              : 'steps.location.searchAddress',
          )}
          initialChoice={initialRef.current?.choice}
          initialAddress={initialRef.current?.address}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  )
}

export const hasOtherLocationValues = (
  x: unknown,
): x is Required<Pick<LocationStepValues, 'otherLocation'>> =>
  R.allPass([R.pipe(R.prop('otherLocation'), hasAddress)])(x)

export default LocationStep
